// Finishing sign-up on the first signed-in session (email confirmation, 2026-10).
//
// With "Confirm email" on, supabase.auth.signUp returns NO session, so nothing
// that needs one can run at the join step. What onboarding collected rides in
// the sign-up metadata instead:
//   • name, role, agency, username, use_username → handle_new_user builds the
//     profile from these when the auth user is created (002/011), as before.
//   • everything else → `pending_signup`, applied here the first time the
//     member is signed in (after confirming, or straight away while
//     confirmation is off — one path either way).
//
// ⚠️ user_metadata is editable by the member (auth.updateUser). That is fine
// because every step below is something they can already do directly: the
// profile fields go through updateProfile's allowlist, the zip through
// set_my_zip_code (054, the only consent path) and the code through
// verify_by_invite_code (058's checks). Never put anything here that grants
// more than the member could call for themselves.

import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';
import { isValidZip } from './zipCode';
import { isDspRole } from './roleLabels';

export const PENDING_KEY = 'pending_signup';

// Where the confirmation link lands. On the web, this site (so local dev and
// previews come back to themselves; Supabase falls back to the project's Site
// URL for anything not on its redirect allow-list). The iOS app's own origin
// (capacitor://localhost) can't be opened from an email, so native leaves it
// to the project's Site URL.
export function confirmRedirect() {
  return Capacitor.isNativePlatform() ? undefined : `${window.location.origin}/`;
}

export function buildPendingSignup({ termsVersion, yearsExperience, zip, code }) {
  return {
    terms_accepted_at: new Date().toISOString(),
    terms_version: termsVersion,
    years_experience: Number.isInteger(yearsExperience) ? yearsExperience : null,
    zip: zip || null,
    code: code || null,
  };
}

const inFlight = new Map();

// Applies pending_signup once, then clears it. Returns null when there was
// nothing to do, otherwise { role, codeResult }. Safe to call on every
// session: concurrent calls for the same user share one run, and a failed
// clear only means the (repeatable) steps run again next time.
export function finishSignup(user, updateProfile) {
  const pending = user?.user_metadata?.[PENDING_KEY];
  if (!user?.id || !pending) return Promise.resolve(null);
  if (inFlight.has(user.id)) return inFlight.get(user.id);

  const run = (async () => {
    const role = user.user_metadata?.role || null;

    const fields = {};
    if (pending.terms_accepted_at) {
      fields.terms_accepted_at = pending.terms_accepted_at;
      fields.terms_version = pending.terms_version;
    }
    if (Number.isInteger(pending.years_experience) && isDspRole(role)) {
      fields.years_experience = pending.years_experience;
    }
    if (Object.keys(fields).length) await updateProfile(fields, user.id);

    // Entering the zip beside ZIP_NOTICE was the opt-in (054). A failure costs
    // only the zip; it can be added from the profile.
    if (isValidZip(pending.zip || '') && isDspRole(role)) {
      const { error } = await supabase.rpc('set_my_zip_code', { p_zip: pending.zip });
      if (error) console.error('[finishSignup] set_my_zip_code failed:', error.message);
    }

    // A ?code= link: verifies only a code an admin marked, and only DSP roles.
    let codeResult;
    if (pending.code) {
      const { data } = await supabase.rpc('verify_by_invite_code', { p_code: pending.code });
      codeResult = data;
    }

    const { error: clearError } = await supabase.auth.updateUser({ data: { [PENDING_KEY]: null } });
    if (clearError) console.error('[finishSignup] could not clear pending_signup:', clearError.message);

    return { role, codeResult };
  })().finally(() => inFlight.delete(user.id));

  inFlight.set(user.id, run);
  return run;
}

// Where a freshly finished member goes: DSPs a code didn't verify get the
// verification offer; everyone else carries on.
export function needsVerifyOffer({ role, codeResult }) {
  return codeResult !== 'ok' && isDspRole(role);
}
